import { useMemo } from 'react';
import {
  Alley,
  AlleySchema,
} from '../../../../../services/entities/impl/blfApi/schemas/alley';
import { Upload } from '../../../../../services/entities/impl/blfApi/schemas/upload';
import { combineValidationResults } from '../../../utils/validation/combineValidationResults';
import {
  ValidationCounts,
  ValidationSummary,
  createValidationSummary,
} from '../../../utils/validation/createValidationSummary';
import { getValidationCounts } from '../../../utils/validation/getValidationCounts';
import {
  ValidationResult,
  validateSchema,
} from '../../../utils/validation/validateSchema';
import { validateAlley } from '../../utils/validateAlley';

/**
 * Hook for validating alleys data
 */
export const useAlleysValidation = (
  alleys: Alley[] | undefined,
  uploads: Upload[] | undefined
): {
  individualResults: Record<string, ValidationResult>;
  summary: ValidationSummary;
} => {
  return useMemo(() => {
    // Initialize with empty results
    const individualResults: Record<string, ValidationResult> = {};

    // Check for empty alleys data
    if (!alleys) {
      const emptyValidationResult: ValidationResult = {
        valid: false,
        messages: [
          {
            severity: 'error',
            path: '',
            message: 'No alleys data found. API endpoint may have failed.',
          },
        ],
      };

      const counts = getValidationCounts(emptyValidationResult);

      return {
        individualResults,
        summary: {
          result: createValidationSummary(counts),
          counts,
        },
      };
    }

    // Check for empty array
    if (alleys.length === 0) {
      const emptyValidationResult: ValidationResult = {
        valid: false,
        messages: [
          {
            severity: 'error',
            path: '',
            message: 'No alleys found. Expected at least one alley.',
          },
        ],
      };

      const counts = getValidationCounts(emptyValidationResult);

      return {
        individualResults,
        summary: {
          result: createValidationSummary(counts),
          counts,
        },
      };
    }

    // Track total counts
    let totalErrors = 0;
    let totalWarnings = 0;

    // Validate each alley
    alleys.forEach((alley) => {
      // 1. Validate against schema
      const schemaResult = validateSchema(alley, AlleySchema);

      // 2. Perform cross-entity validation with uploads
      const crossEntityResult = validateAlley(alley, uploads || []);

      // 3. Combine validation results
      const validationResult = combineValidationResults(
        schemaResult,
        crossEntityResult
      );

      // 4. Store result for this alley
      individualResults[alley.id] = validationResult;

      // 5. Update totals
      const counts = getValidationCounts(validationResult);
      totalErrors += counts.errors;
      totalWarnings += counts.warnings;
    });

    // Create summary counts
    const summaryCounts: ValidationCounts = {
      errors: totalErrors,
      warnings: totalWarnings,
      total: totalErrors + totalWarnings,
    };

    // Create summary result with messages
    const summaryResult = createValidationSummary(summaryCounts);

    return {
      individualResults,
      summary: {
        result: summaryResult,
        counts: summaryCounts,
      },
    };
  }, [alleys, uploads]);
};
